import { defineStore } from 'pinia';
import { ALWAYS, STAFF } from '@/constants/privileges';
import { useSessionStore } from '@/stores/session';

/**
 * The UI's view of what the current user may do.
 *
 * Reduced to a thin lens over the session store, which gets the authoritative
 * permission list from `GET /api/v1/me`. The store it replaces fetched
 * `GetPrivilegeAssignmentsForSubject` from keyon by posting the caller's own
 * `accountnumber` and `unique_ID` in the request body — an endpoint that let any
 * caller enumerate any user's effective privileges, because nothing checked that
 * the requester *was* that user.
 *
 * The shape is deliberately unchanged (`effectiveGranted`, `assignments`,
 * `loading`, `loadedKey`, `fetchForCurrentSession`), so the components using it
 * keep working while they migrate.
 */
export const usePrivilegesStore = defineStore('privileges', {
  getters: {
    /**
     * Whether the UI should offer an action.
     *
     * `ALWAYS` entries are nominal rights rather than authorization — the help
     * viewer, "may log in" — and are not gated at all. `STAFF` is true for
     * anyone who is neither a patient nor a patient's contact, and false until
     * the session says which they are: an unknown account type must not start
     * the billable timer on a patient's screen.
     *
     * The session's `can`, so what a single-chart emergency incident lends is
     * offered while that chart is in view, as the navigation offers it.
     */
    effectiveGranted() {
      const session = useSessionStore();

      return (permission) => {
        if (!permission) return false;
        if (permission === ALWAYS) return true;
        if (permission === STAFF) {
          const type = session.accountType;
          return Boolean(type) && type !== 'patient' && type !== 'contact';
        }
        return session.can(permission);
      };
    },

    /** Kept for the admin screens that render a permission list. */
    assignments() {
      const session = useSessionStore();

      return session.permissions.map((code) => ({
        id: code,
        code,
        effective_granted: true,
        user_override: null,
      }));
    },

    rowById() {
      const session = useSessionStore();

      return (permission) => (session.permissions.includes(permission)
        ? { id: permission, code: permission, effective_granted: true, user_override: null }
        : null);
    },

    /**
     * Non-empty once `GET /me` has answered: the "permissions are settled"
     * signal. The directives pages watch it and the router guard, My Org and
     * the privileges page test it, reading `''` as "not loaded yet" rather than
     * "holds nothing". It is set for an actor who holds no permissions at all,
     * which `assignments.length` cannot tell apart from a load still running.
     */
    loadedKey() {
      const session = useSessionStore();

      return session.me ? `${session.organizationId}:${session.userId}` : '';
    },

    loading() {
      return useSessionStore().loading;
    },

    /** The `GET /me` concurrent callers are joining, or null when none is. */
    inflight() {
      return useSessionStore().inflight;
    },

    error() {
      return useSessionStore().error;
    },
  },

  actions: {
    /**
     * Retained so existing callers still resolve. Reloading permissions means
     * reloading the session — there is no separate privileges endpoint, and no
     * identity to pass, because the server reads it from the token.
     */
    async load() {
      return useSessionStore().hydrate();
    },

    /**
     * Load the permission set unless it is already in hand.
     *
     * What `fetchForCurrentSession(phrData)` callers wanted — "make sure
     * permissions are loaded, without paying for it twice" — without the
     * legacy blob it keyed a cache on: the token says who the caller is.
     * Concurrent callers share one `GET /me`, because `session.hydrate()`
     * joins a load already in flight.
     */
    async ensureLoaded() {
      const session = useSessionStore();

      if (session.me) return session.me;

      return session.hydrate();
    },

    /**
     * The name main.js, My Org, the privileges page, the directives pages and
     * useSchedulingAccess call. The session snapshot they pass is ignored; see
     * `ensureLoaded`.
     */
    async fetchForCurrentSession() {
      // Main's callers await this without a catch. A failed load resolves
      // rather than rejects, and leaves `loadedKey` empty — which every gate
      // reads as "not settled yet", so the page stays retryable instead of
      // settling on a refusal — with the reason on `error`.
      try {
        return await this.ensureLoaded();
      } catch (e) {
        return null;
      }
    },

    clear() {
      useSessionStore().$reset();
    },
  },
});

export default usePrivilegesStore;
